/**
 * Avatares: personagens desenhados, servidos como arquivos.
 *
 * A escolha é entre opções, não upload. A razão está na migration
 * `20260901000100`, e vale repetir aqui porque é uma decisão de produto
 * que a interface poderia desfazer sem perceber: foto enviada por usuário
 * numa plataforma com dinheiro na mesa traz moderação, armazenamento e
 * abuso — e o abuso chega como denúncia, não como alerta.
 *
 * Os arquivos vivem em `web/public/avatares/` e chegam por `<img>`, não
 * colados dentro do JavaScript. Três motivos, em ordem de peso:
 *
 * 1. O navegador baixa só os que aparecem na tela. Colados no pacote,
 *    todos viajam para todo mundo, inclusive os que ninguém escolheu.
 * 2. Trocar um desenho não obriga ninguém a rebaixar o aplicativo
 *    inteiro: muda um arquivo, e só ele.
 * 3. SVG colado na página divide o espaço de identificadores com os
 *    outros. Dois avatares com um gradiente chamado `grad1` viram um só
 *    na tela — e o defeito aparece justamente quando estão lado a lado,
 *    que é o caso do salão.
 *
 * O catálogo vem de `catalogoAvatares.ts`, gerado por
 * `scripts/avatares.sh` a partir da pasta. Ele espelha o `check` da
 * coluna `avatar`; se divergirem, quem manda é o banco — a tela mostraria
 * alguém que a gravação recusa.
 */
import { useState } from 'react';

import { AVATARES, type AvatarId } from './catalogoAvatares.ts';

export { AVATARES };
export type { AvatarId };

export const CORES_AVATAR = [
  'areia',
  'nevoa',
  'menta',
  'lilas',
  'coral',
  'ardosia',
] as const;

export type CorAvatar = (typeof CORES_AVATAR)[number];

export const NOME_DA_COR: Record<CorAvatar, string> = {
  areia: 'Areia',
  nevoa: 'Névoa',
  menta: 'Menta',
  lilas: 'Lilás',
  coral: 'Coral',
  ardosia: 'Ardósia',
};

/**
 * Fundos das telhas.
 *
 * Claros de propósito: o desenho chega sem fundo, com traço escuro, e
 * traço escuro sobre fundo escuro some. O avatar aparece a 30px no
 * cabeçalho, onde não há espaço para resolver contraste com detalhe.
 *
 * Escritos aqui e não em token de tema porque precisam ser os mesmos nos
 * dois temas — o avatar é identidade da pessoa, e identidade que muda de
 * cor quando o outro jogador está no modo escuro não identifica ninguém.
 *
 * Os mesmos valores estão em docs/AVATARES.md, para quem desenha
 * conferir o contraste antes de exportar.
 */
const FUNDO: Record<CorAvatar, string> = {
  areia: 'oklch(0.93 0.03 85)',
  nevoa: 'oklch(0.93 0.02 250)',
  menta: 'oklch(0.92 0.04 160)',
  lilas: 'oklch(0.92 0.04 300)',
  coral: 'oklch(0.92 0.04 30)',
  ardosia: 'oklch(0.88 0.01 250)',
};

/**
 * Nome legível de um avatar, a partir do identificador do arquivo.
 *
 * O prefixo numérico sai: ele existe para fixar a ordem do catálogo, que
 * é a da prancha do designer, e não para ser lido. `03-jaqueta-mostarda`
 * vira "Jaqueta mostarda".
 */
export function nomeDoAvatar(id: string): string {
  const limpo = id.replace(/^\d+-/, '').replace(/-/g, ' ').trim();
  return limpo.charAt(0).toUpperCase() + limpo.slice(1);
}

export interface AvatarProps {
  readonly avatar?: string | null;
  readonly cor?: string | null;
  /** Diâmetro em pixels. */
  readonly tamanho?: number;
  /**
   * Nome de quem o avatar representa. Vira o rótulo acessível — sem ele,
   * o leitor de tela anuncia "imagem" e a pessoa não sabe de quem é.
   */
  readonly nome?: string;
  readonly className?: string;
  /**
   * Carrega assim que a página monta, em vez de esperar entrar na tela.
   *
   * O padrão é preguiçoso, que é certo numa lista longa como a do salão.
   * No seletor do perfil é o contrário: são 48 de uma vez, e quem rola vê
   * círculos vazios se enchendo — que parece defeito, não otimização.
   */
  readonly ansioso?: boolean;
}

function valido<T extends string>(lista: readonly T[], valor: unknown, padrao: T): T {
  return typeof valor === 'string' && (lista as readonly string[]).includes(valor)
    ? (valor as T)
    : padrao;
}

export function Avatar({ avatar, cor, tamanho = 40, nome, className, ansioso }: AvatarProps) {
  // Arquivo que não carrega não pode virar ícone de imagem quebrada na
  // mesa: a pessoa continua tendo um lugar, mesmo que sem rosto.
  const [falhou, setFalhou] = useState(false);

  // O que vem do servidor é validado contra o catálogo antes de virar
  // caminho de arquivo. Sem isso, um valor inesperado viraria uma busca
  // por um arquivo que não existe — e, pior, o valor entraria na URL.
  const id = valido(AVATARES, avatar, AVATARES[0] as AvatarId);
  const fundo = FUNDO[valido(CORES_AVATAR, cor, 'areia')];

  return (
    <span
      className={['inline-grid place-items-center rounded-full shrink-0 overflow-hidden', className ?? ''].join(' ')}
      style={{
        width: tamanho,
        height: tamanho,
        background: fundo,
        boxShadow: 'inset 0 0 0 1px oklch(0 0 0 / 0.1)',
      }}
      role="img"
      aria-label={nome ? `Avatar de ${nome}` : `Avatar: ${nomeDoAvatar(id)}`}
    >
      {falhou ? null : (
        <img
          src={`/avatares/${id}.svg`}
          alt=""
          width={tamanho}
          height={tamanho}
          // O rótulo acessível está no elemento de fora; repetir aqui faria
          // o leitor de tela anunciar duas vezes.
          aria-hidden="true"
          draggable={false}
          // Numa lista de mesas há muitos avatares, e a maioria fica fora
          // da tela até se rolar até eles.
          loading={ansioso ? 'eager' : 'lazy'}
          decoding="async"
          onError={() => setFalhou(true)}
          style={{ display: 'block', width: tamanho, height: tamanho }}
        />
      )}
    </span>
  );
}

/** Avatar com o nome ao lado. O par mais repetido da interface. */
export function AvatarComNome({
  avatar,
  cor,
  nome,
  tamanho = 36,
  detalhe,
}: {
  avatar?: string | null;
  cor?: string | null;
  nome: string;
  tamanho?: number;
  detalhe?: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-3 min-w-0">
      <Avatar avatar={avatar} cor={cor} tamanho={tamanho} nome={nome} />
      <div className="min-w-0">
        <p className="font-semibold truncate">{nome}</p>
        {detalhe ? <div className="text-sm text-muted-foreground truncate">{detalhe}</div> : null}
      </div>
    </div>
  );
}
